import React, { useMemo, useState } from "react";
import {
  Button,
  Container,
  MenuItem,
  Select,
  Typography,
  Box,
  FormControl,
  InputLabel,
  TextField,
  Divider,
} from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { isUsMarket } from "utils/market";
import downloadReport from "./downloadReport";

const generateMonthOptions = () => {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-GB", { year: "numeric", month: "long" });
    options.push({ value, label });
  }
  return options;
};

const generateQuarterOptions = () => {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  let year = now.getFullYear();
  let q = currentQ - 1;
  if (q < 1) {
    q = 4;
    year--;
  }
  for (let i = 0; i < 8; i++) {
    options.push({ value: `${year}-Q${q}`, label: `Q${q} ${year}` });
    q--;
    if (q < 1) {
      q = 4;
      year--;
    }
  }
  return options;
};

const UkReportsPage: React.VFC = () => {
  const { t } = useTranslation();
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);
  const [month, setMonth] = useState(monthOptions[0].value);
  const [quarter, setQuarter] = useState(quarterOptions[0].value);
  const [reconcileMonth, setReconcileMonth] = useState(monthOptions[0].value);
  const [invoiceIds, setInvoiceIds] = useState("");
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (isUsMarket) return null;

  const handleDownload = async (type: "mds" | "sqpr" | "crm") => {
    setLoading(type);
    setError(null);
    try {
      if (type === "crm") {
        await downloadReport(`/uk/reports/crm?quarter=${quarter}`, `uk_crm_${quarter}.csv`);
      } else {
        await downloadReport(`/uk/reports/${type}?month=${month}`, `uk_${type}_${month}.csv`);
      }
    } catch (e) {
      setError(t("uk_reports.download_failed", { type: type.toUpperCase() }));
    } finally {
      setLoading(null);
    }
  };

  const handleReconcile = async () => {
    if (!invoiceIds.trim()) {
      setError(t("uk_reports.paste_ids_required"));
      return;
    }
    setLoading("reconcile");
    setError(null);
    try {
      await downloadReport(`/uk/reports/mds_reconcile`, `uk_mds_reconcile_${reconcileMonth}.csv`, {
        method: "POST",
        data: { invoice_ids: invoiceIds, month: reconcileMonth },
      });
    } catch (e) {
      setError(t("uk_reports.reconcile_failed"));
    } finally {
      setLoading(null);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box mt={3} mb={4}>
        <Typography variant="h5" gutterBottom>
          {t("uk_reports.title")}
        </Typography>
      </Box>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <Box mb={4}>
        <Typography variant="subtitle1" gutterBottom>
          {t("uk_reports.monthly")}
        </Typography>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel>{t("uk_reports.month")}</InputLabel>
          <Select value={month} onChange={e => setMonth(e.target.value as string)} label={t("uk_reports.month")}>
            {monthOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box mt={2} display="flex" style={{ gap: 16 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={() => handleDownload("mds")}
            disabled={loading !== null}
          >
            {loading === "mds" ? t("uk_reports.downloading") : t("uk_reports.mds")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={() => handleDownload("sqpr")}
            disabled={loading !== null}
          >
            {loading === "sqpr" ? t("uk_reports.downloading") : t("uk_reports.sqpr")}
          </Button>
        </Box>
      </Box>

      <Box mb={4}>
        <Typography variant="subtitle1" gutterBottom>
          {t("uk_reports.quarterly")}
        </Typography>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel>{t("uk_reports.quarter")}</InputLabel>
          <Select value={quarter} onChange={e => setQuarter(e.target.value as string)} label={t("uk_reports.quarter")}>
            {quarterOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={() => handleDownload("crm")}
            disabled={loading !== null}
          >
            {loading === "crm" ? t("uk_reports.downloading") : t("uk_reports.crm")}
          </Button>
        </Box>
      </Box>

      <Divider />

      <Box mt={4} mb={4}>
        <Typography variant="subtitle1" gutterBottom>
          {t("uk_reports.reconciliation")}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {t("uk_reports.reconciliation_help")}
        </Typography>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel>{t("uk_reports.month")}</InputLabel>
          <Select
            value={reconcileMonth}
            onChange={e => setReconcileMonth(e.target.value as string)}
            label={t("uk_reports.month")}
          >
            {monthOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box mt={2}>
          <TextField
            variant="outlined"
            fullWidth
            multiline
            rows={6}
            placeholder={t("uk_reports.invoice_ids_placeholder")}
            value={invoiceIds}
            onChange={e => setInvoiceIds(e.target.value)}
          />
        </Box>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetApp />}
            onClick={handleReconcile}
            disabled={loading !== null || !invoiceIds.trim()}
          >
            {loading === "reconcile" ? t("uk_reports.downloading") : t("uk_reports.reconcile")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default UkReportsPage;
